import {} from 'react';
import isCursorInBoundary from '~/util/isCursorInBoundary';
import useOnClickOutside from '../useOnClickOutside';
import type { RootId } from '~/components/molecule/Portal';

/**
 * Helps to use useOnClickOutside for PortalledPositioner.
 * Use this hook instead of useOnClickOutside for PortalledPositioner.
 *
 * @param {React.RefObject<HTMLElement>} props.openerRef - The ref of the opener element
 * @param {RootId} props.rootId - The id of the element that is the root of the portalled positioner
 * @param {() => void} props.onClose - The function to call when the click is outside the opener
 */
const useOnClickOutsideForPortalledPositioner = ({
  openerRef,
  rootId,
  onClose,
}: {
  openerRef: React.RefObject<HTMLElement>;
  rootId: RootId;
  onClose: () => void;
}) => {
  useOnClickOutside(openerRef, e => {
    if (!openerRef.current) return;

    const openerRect = openerRef.current.getBoundingClientRect() ?? null;

    if (!openerRect) return;

    const isClickOnOpener = isCursorInBoundary({
      event: e,
      boundary: openerRect,
    });

    if (isClickOnOpener) return;

    // Check if the click is on the root element
    const portalRoot = document.getElementById(`${rootId}-root`);

    if (
      e.target instanceof Node &&
      portalRoot &&
      portalRoot.contains(e.target)
    ) {
      return;
    }

    onClose();
  });
};

export default useOnClickOutsideForPortalledPositioner;
